import { useState, useEffect, useRef } from 'react';
import type { SessionListItem } from '@roll1/shared';
import { apiFetch } from '../../lib/api';
import { Button } from '../ui/button';
import { useI18n, pluralize } from '../../i18n';
import { MoreHorizontal, Pencil, Trash2, Link2 } from 'lucide-react';
import InputDialog from '../InputDialog';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction,
  AlertDialogCancel,
} from '../ui/alert-dialog';

function formatDate(iso: string, t: (key: any) => string) {
  const d = new Date(iso);
  const now = new Date();
  const diffMs = now.getTime() - d.getTime();
  const diffDays = Math.floor(diffMs / 86400000);
  if (diffDays === 0) return t('sessionList.today');
  if (diffDays === 1) return t('sessionList.yesterday');
  if (diffDays < 7) return `${diffDays} ${pluralize(diffDays, t('plural.day_one'), t('plural.day_few'), t('plural.day_many'))} ${t('sessionList.daysAgo')}`;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
}

interface SessionListPanelProps {
  onJoinSession: (code: string, name: string) => void;
  userName: string;
}

export default function SessionListPanel({ onJoinSession, userName }: SessionListPanelProps) {
  const { t } = useI18n();
  const [sessions, setSessions] = useState<SessionListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [copiedCode, setCopiedCode] = useState<string | null>(null);
  const [activeMenuId, setActiveMenuId] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // Rename dialog state
  const [renameDialog, setRenameDialog] = useState<{ open: boolean; sessionId: string; currentName: string }>({
    open: false, sessionId: '', currentName: '',
  });

  // Delete dialog state
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; sessionId: string; sessionName: string }>({
    open: false, sessionId: '', sessionName: '',
  });

  useEffect(() => {
    (async () => {
      try {
        const res = await apiFetch('/api/auth/sessions');
        if (res.ok) {
          const data = await res.json();
          setSessions(data);
        }
      } catch {
        // Ignore — user sees empty state
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Close menu on click outside
  useEffect(() => {
    if (!activeMenuId) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setActiveMenuId(null);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [activeMenuId]);

  const handleCopyCode = (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    navigator.clipboard.writeText(code);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const handleCopyInvite = (e: React.MouseEvent, code: string) => {
    e.stopPropagation();
    const url = `${window.location.origin}?join=${code}`;
    navigator.clipboard.writeText(url);
    setCopiedCode(code);
    setTimeout(() => setCopiedCode(null), 2000);
    setActiveMenuId(null);
  };

  const handleRename = async (newName: string) => {
    const { sessionId, currentName } = renameDialog;
    setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, name: newName } : s));
    try {
      const res = await apiFetch(`/api/auth/sessions/${sessionId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: newName }),
      });
      if (!res.ok) {
        setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, name: currentName } : s));
      }
    } catch {
      setSessions(prev => prev.map(s => s.id === sessionId ? { ...s, name: currentName } : s));
    }
  };

  const handleDelete = async () => {
    const { sessionId } = deleteConfirm;
    const backup = sessions;
    setSessions(prev => prev.filter(s => s.id !== sessionId));
    setDeleteConfirm({ open: false, sessionId: '', sessionName: '' });
    try {
      const res = await apiFetch(`/api/auth/sessions/${sessionId}`, { method: 'DELETE' });
      if (!res.ok) {
        setSessions(backup);
      }
    } catch {
      setSessions(backup);
    }
  };

  if (loading) {
    return <p className="session-list-loading">{t('sessionList.loading')}</p>;
  }

  if (sessions.length === 0) {
    return null;
  }

  return (
    <div className="session-list">
      <h3 className="session-list-title">{t('sessionList.title')}</h3>
      {sessions.map(s => {
        const count = s.participantCount;
        const countLabel = `${count} ${pluralize(count, t('plural.participant_one'), t('plural.participant_few'), t('plural.participant_many'))}`;
        const isGM = s.role === 'gm';
        return (
          <div
            key={s.id}
            className="session-list-item"
            onClick={() => onJoinSession(s.code, userName)}
          >
            <div className="session-list-item-info">
              <div className="session-list-item-header">
                <span className="session-list-item-name">
                  {t('sessionList.session')} {s.name}
                </span>
                <span className="session-list-item-role">
                  {isGM ? 'GM' : t('sessionList.player')}
                </span>
              </div>
              <div className="session-list-item-meta">
                <button
                  className="session-list-item-code"
                  onClick={(e) => handleCopyCode(e, s.code)}
                  title={t('sessionList.copyCode')}
                >
                  {copiedCode === s.code ? t('sessionList.copied') : s.code}
                </button>
                <span className="session-list-item-dot">&middot;</span>
                <span>{countLabel}</span>
                <span className="session-list-item-dot">&middot;</span>
                <span>{formatDate(s.createdAt, t)}</span>
              </div>
            </div>

            {/* Context menu button */}
            <div className="session-list-item-actions" style={{ position: 'relative' }}>
              <button
                className="session-list-item-menu-btn"
                data-testid={`session-menu-${s.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setActiveMenuId(prev => prev === s.id ? null : s.id);
                }}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
                  color: 'var(--muted-foreground)', display: 'flex', alignItems: 'center',
                }}
              >
                <MoreHorizontal size={16} />
              </button>

              {activeMenuId === s.id && (
                <div
                  ref={menuRef}
                  className="session-list-menu"
                  data-testid={`session-menu-popup-${s.id}`}
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    position: 'absolute', right: 0, top: '100%', zIndex: 'var(--z-overlay)',
                    background: 'var(--popover)', border: '1px solid var(--border)',
                    borderRadius: '6px', padding: '4px 0', minWidth: '180px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                  }}
                >
                  {isGM && (
                    <button
                      className="session-list-menu-item"
                      data-testid={`session-rename-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(null);
                        setRenameDialog({ open: true, sessionId: s.id, currentName: s.name });
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '6px 12px', background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--foreground)', fontSize: '13px', textAlign: 'left',
                      }}
                    >
                      <Pencil size={14} /> {t('sessionList.rename')}
                    </button>
                  )}
                  {isGM && (
                    <button
                      className="session-list-menu-item"
                      data-testid={`session-delete-${s.id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        setActiveMenuId(null);
                        setDeleteConfirm({ open: true, sessionId: s.id, sessionName: s.name });
                      }}
                      style={{
                        display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                        padding: '6px 12px', background: 'none', border: 'none', cursor: 'pointer',
                        color: 'var(--destructive)', fontSize: '13px', textAlign: 'left',
                      }}
                    >
                      <Trash2 size={14} /> {t('sessionList.delete')}
                    </button>
                  )}
                  <button
                    className="session-list-menu-item"
                    data-testid={`session-copy-invite-${s.id}`}
                    onClick={(e) => handleCopyInvite(e, s.code)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '8px', width: '100%',
                      padding: '6px 12px', background: 'none', border: 'none', cursor: 'pointer',
                      color: 'var(--foreground)', fontSize: '13px', textAlign: 'left',
                    }}
                  >
                    <Link2 size={14} /> {t('sessionList.copyInvite')}
                  </button>
                </div>
              )}
            </div>
          </div>
        );
      })}

      {/* Rename Dialog */}
      <InputDialog
        open={renameDialog.open}
        onOpenChange={(open) => setRenameDialog(prev => ({ ...prev, open }))}
        title={t('sessionList.rename')}
        defaultValue={renameDialog.currentName}
        onConfirm={handleRename}
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => setDeleteConfirm(prev => ({ ...prev, open }))}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('sessionList.deleteConfirmTitle')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('sessionList.deleteConfirm')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete}>{t('common.delete')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
